import { IcbtTasks } from "api/types";
import { Patient } from "core/types/index";
import { isThisMonth, isThisWeek } from "date-fns";
import { useMemo } from "react";
import { filter, map, partition, pipe, sortBy } from "remeda";

export const doneCommunicationCategories = [
  "week",
  "month",
  "all",
  "noFeedbackNeeded",
] as const;
export type Categories = (typeof doneCommunicationCategories)[number];

export const categoriesWithAssessments = ["now", "all"] as const;
export type CategoriesWithAssessments =
  (typeof categoriesWithAssessments)[number];

type BasePatientData = { userId: number };

export type IcbtTasksWithPatient = IcbtTasks & {
  patient?: Patient;
};

export const useSortedIcbtDoneTasks = <PatientData extends BasePatientData>({
  icbtTasks,
  patientsToIncludeInCommunication: patients,
}: {
  icbtTasks?: IcbtTasks[];
  patientsToIncludeInCommunication?: PatientData[];
}) =>
  useMemo(() => {
    if (!icbtTasks)
      return new Map(
        Object.entries({
          week: [],
          month: [],
          all: [],
          noFeedbackNeeded: [],
        }),
      );
    const sorted = pipe(
      icbtTasks,
      filter(
        (request) =>
          Boolean(request.feedbackDate) &&
          request.tasks.length < 1 &&
          request.status === "done" &&
          (!!request.feedbackPublishedAt || request.feedbackDateIgnored),
      ),
      // Include patient data
      map((request) => {
        return {
          ...request,
          patient: patients?.find(
            (p) => p.userId === Number(request.patientUserId),
          ),
        };
      }),
      // Partition to 'noFeedbackNeeded' based on feedbackDateIgnored
      partition((request) => Boolean(request.feedbackDateIgnored)),
      // Partition the rest into week and month
      ([noFeedbackNeeded, rest]) => [
        noFeedbackNeeded,
        ...partition(rest, (r) =>
          isThisWeek(new Date(r.feedbackDate), { weekStartsOn: 1 }),
        ),
      ],
      // Partition the rest into this month and final rest
      ([noFeedbackNeeded, week, rest]) => [
        noFeedbackNeeded,
        week,
        ...partition(rest, (r) => isThisMonth(new Date(r.feedbackDate))),
      ],
      // Include 'week' to 'month' and 'month' to 'all', but exclude 'noFeedbackNeeded'
      ([noFeedbackNeeded, week, month, all]) => [
        noFeedbackNeeded,
        week,
        [...week, ...month],
        [...week, ...month, ...all],
      ],
      // Sort each category by feedback_date
      map((category) => {
        return pipe(
          category,
          sortBy((c) => -new Date(c.feedbackDate).getTime()),
        );
      }),
      // Return a map with categories
      ([noFeedbackNeeded, week, month, all]) =>
        new Map<Categories, IcbtTasksWithPatient[]>(
          Object.entries({
            noFeedbackNeeded,
            week,
            month,
            all,
          }) as [Categories, IcbtTasksWithPatient[]][],
        ),
    );
    return sorted;
  }, [patients, icbtTasks]);
